import { z } from 'zod';
import { appointmentDraftSchema, appointmentSchema } from './appointment.js';
import { optionalText, requiredText } from './field.js';
import { cursorSchema, PAGE_SIZE } from './list.js';
/**
 * A dated event where services were rendered to a contact — a session, a talk,
 * a consultation. The activity is the record of what happened and the primary
 * place to make corrections (rule 6 in `.claude/rules/billing.md`).
 *
 * `type` is the `code` of an `activity_type`, a catalogue the practitioner
 * maintains, reached through a composite foreign key. Neither an enum nor a
 * check constraint: the set is not merely expected to change, it is owned by
 * the practice.
 */
/**
 * What became of the activity. Descriptive only — **it does not gate billing**.
 * Anything in the past can be invoiced whatever this says, and
 * `domain/billable.ts` does not read it; billability is a property of the item
 * (`activity_item.billable`), per rule 6 in `.claude/rules/billing.md`.
 *
 * Separate from `appointment.status`, which says what became of the *slot*.
 * A no-show is an activity that did not take place in a slot that stayed
 * occupied, and before the split neither status could say that.
 */
export const activityStatuses = ['planned', 'rendered', 'no_show'];
export const activityStatusSchema = z.enum(activityStatuses);
/**
 * Whether what was rendered here has been claimed yet.
 *
 * Derived on read from the invoice lines and **never stored** — a column would
 * be a second place saying what the lines already say, and a cancelled invoice
 * frees its items again (rule 9) without anything to keep in step. It comes
 * from `billingStateOf()` in `domain/billable.ts`, which uses the very same
 * condition as the billable list; see the note there.
 *
 * `none` is not "nothing has been billed" but "there is nothing to bill" — an
 * activity whose items are all marked unbillable, or one without items.
 */
export const activityBillingStates = ['none', 'open', 'billed'];
export const activityBillingStateSchema = z.enum(activityBillingStates);
const quantity = z.number().int().positive().max(999);
const durationMin = z
    .number()
    .int()
    .positive()
    .max(24 * 60)
    .nullable()
    .default(null);
/**
 * How a position is submitted. The same union serves creating and editing,
 * and the copying happens on the server — the rule that a service is a
 * template belongs in `domain/`, not in a form.
 *
 * - `service` — the domain copies description, fee code and price out of the
 *   catalogue. `service_id` is kept as a record of origin and means nothing
 *   for price or text afterwards (rule 5).
 * - `group` — resolved into individual items immediately. No group id is
 *   stored anywhere; renaming or emptying the group later changes nothing.
 * - `custom` — taken as given. A one-off talk, an adjusted price, or an item
 *   that already exists and is being edited. `id` present means "this row",
 *   absent means "a new one".
 */
export const activityItemInputSchema = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('service'),
        serviceId: z.uuid(),
        quantity: quantity.default(1),
        billable: z.boolean().default(true),
    }),
    z.object({
        kind: z.literal('group'),
        serviceGroupId: z.uuid(),
    }),
    z.object({
        kind: z.literal('custom'),
        id: z.uuid().optional(),
        serviceId: z.uuid().nullable().default(null),
        description: requiredText(200),
        feeCode: optionalText(40),
        quantity: quantity.default(1),
        // No sign restriction, unlike the catalogue: rule 5 handles discounts by
        // leaving the price here free, and a negative one-off line is the way.
        unitPriceCents: z.number().int().min(-100_000_000).max(100_000_000),
        billable: z.boolean().default(true),
    }),
]);
export const activityInputSchema = z.object({
    contactId: z.uuid(),
    /** The `activity_type` this is one of. An id since migration 0041; the
     *  catalogue has no code anymore. */
    activityTypeId: z.uuid(),
    status: activityStatusSchema.default('planned'),
    occurredAt: z.iso.datetime(),
    /** Descriptive only. Nothing is derived from it — an activity documented
     *  afterwards has no appointment to take a length from. */
    durationMin,
    /** Optional: where it is missing, every screen shows the label of the
     *  activity type instead — see `activityLabel`. */
    title: optionalText(200),
    internalNote: optionalText(4000),
    items: z.array(activityItemInputSchema).max(100).default([]),
    /** Created together with the activity by default; `null` documents
     *  something that never produced a calendar entry. */
    appointment: appointmentDraftSchema.nullable().default(null),
});
export const activityItemSchema = z.object({
    id: z.uuid(),
    position: z.number().int(),
    serviceId: z.uuid().nullable(),
    description: z.string(),
    feeCode: z.string().nullable(),
    quantity: z.number().int(),
    unitPriceCents: z.number().int(),
    billable: z.boolean(),
});
export const activitySchema = z.object({
    id: z.uuid(),
    contactId: z.uuid(),
    /** Who it was for. On the practice-wide list this is the first thing read —
     *  it is the only column that says the rows apart — and it comes along here
     *  rather than through a second request, the same way `invoice.contactName`
     *  does. `formatContactName()` on the server, so the list and the invoice
     *  spell a name the same way. */
    contactName: z.string(),
    contactNumber: z.number().int(),
    activityTypeId: z.uuid(),
    status: activityStatusSchema,
    occurredAt: z.iso.datetime(),
    durationMin: z.number().int().nullable(),
    title: z.string().nullable(),
    internalNote: z.string().nullable(),
    appointment: appointmentSchema.nullable(),
    items: z.array(activityItemSchema),
    billingState: activityBillingStateSchema,
});
/**
 * An appointment in a list, with just enough of its contact and activity to
 * paint a calendar without a second round trip.
 *
 * It lives here rather than next to `appointmentSchema` because it needs both
 * halves, and this is the file that may see both: `activity.ts` imports
 * `appointment.ts`, so the reverse import would close a cycle.
 *
 * The activity's type travels as its **id**, not as a label and not as a
 * colour — the client has the catalogue loaded for the filter anyway, and
 * resolving it there keeps one source for both. It was the code until
 * migration 0041 took the code off the catalogue. All four activity columns are
 * null on a free-standing appointment — a blocker, documentation time, a team
 * meeting, which `POST /api/appointments` has produced since D-K1.
 */
export const calendarEntrySchema = appointmentSchema.extend({
    activityId: z.uuid().nullable(),
    activityTypeId: z.uuid().nullable(),
    activityStatus: activityStatusSchema.nullable(),
    /** The Vorgang's own title, which is a different column from the
     *  appointment's `title` above: an appointment created together with its
     *  activity carries none of its own. Read by the contact's overview, which
     *  names the next appointment the way every other screen names a Vorgang —
     *  `activityLabel()`, title before type label. */
    activityTitle: z.string().nullable(),
    /** Both null on an appointment that belongs to nobody. What the block then
     *  shows is its title — which is why a bare appointment has one. */
    contactNumber: z.number().int().nullable(),
    contactName: z.string().nullable(),
});
/** Either by contact or by date range; the route requires at least one, so a
 *  bare call cannot walk the whole history. */
/**
 * Which half of the list is being asked for (L3).
 *
 * The two halves get **different rules**, not one order with a break in it,
 * because they are different in kind: the future is finite and the past is
 * not.
 *
 * - `upcoming` — everything from now on, ascending, **unpaged**. A practice
 *   has as many future appointments as it has booked, which is a number a
 *   screen can hold. There is deliberately no cap: a cap that is reached
 *   drops rows silently, while an uncapped list that ever grows unwieldy says
 *   so by being slow, and that is the failure one can act on.
 * - `past` — everything before now, newest first, paged.
 *
 * Absent means neither: the whole range, newest first, paged. That is what a
 * picker asks for — the note dialog choosing an activity does not care which
 * side of now it is on.
 */
/**
 * Whether an activity still owes money or has been claimed, as a *filter*
 * (L7). Two values and not three: `none` — nothing billable at all — is not
 * something anybody narrows a list to, and offering it would put a chip on
 * screen whose answer is "the rows without positions".
 *
 * It is the same statement `activity.billingState` makes on each row, and the
 * server answers both from one condition; see `activityBillingCondition` in
 * `domain/billable.ts`.
 */
export const activityBillingFilters = ['billed', 'open'];
export const activityBillingFilterSchema = z.enum(activityBillingFilters);
export const activityListParts = ['upcoming', 'past'];
export const activityListPartSchema = z.enum(activityListParts);
/**
 * **Every bound is optional**, the same as on the summary beside it (B2).
 *
 * It carried `contactId or from is required` until then, and the Vorgänge page
 * satisfied it by prefilling a window of 120 days — a range nobody had asked
 * for, in a form that read as a record of one. Nothing in the query needs the
 * bound: the past is cut into pages by keyset, and the future is finite, which
 * is the whole reason it comes whole (L3). What the refusal actually bought was
 * a default the screen then had to claim.
 */
export const activityListQuerySchema = z.object({
    contactId: z.uuid().optional(),
    from: z.iso.datetime().optional(),
    to: z.iso.datetime().optional(),
    status: activityStatusSchema.optional(),
    /** One `activity_type`, by id (D8). Filtered on the server like the
     *  status, and for the same reason: the list is paged. */
    activityTypeId: z.uuid().optional(),
    /** Filtered on the server like the status, and for the same reason: the
     *  list is paged, and a browser cannot narrow what it never fetched. */
    billing: activityBillingFilterSchema.optional(),
    part: activityListPartSchema.optional(),
    limit: z.coerce.number().int().min(1).max(200).default(PAGE_SIZE),
    /** Absent means the first page. Ignored for `upcoming`, which has none. */
    cursor: cursorSchema.optional(),
});
/**
 * The same selection the list is drawn for, without the paging — a summary is
 * an aggregate over the whole of it or it says nothing.
 *
 * **Every bound is optional** (L7). The Vorgänge page asks about a window and
 * passes both; a contact's Vorgänge tab asks about a contact, whose whole
 * history is the window, and passes neither. Requiring `from`/`to` there would
 * have meant inventing a range to count over — and the counts would then have
 * disagreed with the list, which has no range either.
 *
 * What the summary must **not** carry is the selection the chips make: the
 * figures describe what there is, so picking a chip cannot change the number
 * written on it. `type` is the exception and belongs here, because that filter
 * sits above the chips rather than among them.
 */
export const activitySummaryQuerySchema = z.object({
    contactId: z.uuid().optional(),
    from: z.iso.datetime().optional(),
    to: z.iso.datetime().optional(),
    activityTypeId: z.uuid().optional(),
});
/**
 * What the Vorgänge page says above the list: how many there are, how they
 * split by status, how many are still ahead, and what has been rendered and
 * not yet claimed.
 *
 * The status counts are what the filter chips carry, so switching a chip does
 * not change them — they describe the window, not the selection.
 */
export const activitySummarySchema = z.object({
    total: z.number().int(),
    planned: z.number().int(),
    rendered: z.number().int(),
    noShow: z.number().int(),
    /** Still ahead — `occurredAt` at or after the moment of asking, which is the
     *  same rule the list's "Kommend" section uses. Compared as an instant and
     *  not as a day on purpose: at ten in the morning, the nine o'clock session
     *  has happened, and a list that still calls it upcoming is wrong about the
     *  one thing this number is for. */
    upcoming: z.number().int(),
    /** Rendered and not yet on a non-cancelled invoice. The one figure here that
     *  is money, and the reason the line is worth reading at all. */
    unbilledCents: z.number().int(),
    /** How many activities are wholly claimed, and how many still owe something
     *  (L7) — the two figures the last pair of chips carries. They do not add up
     *  to `total`: an activity with no billable position at all is neither, and
     *  that is `billingState === 'none'`. */
    billed: z.number().int(),
    unbilled: z.number().int(),
});
/**
 * What to call an activity on screen: its own title, or the label of its type.
 *
 * `title` is optional, and the fallback has to be the same everywhere — list,
 * calendar, contact overview, note dialog — so it lives here rather than being
 * written out four times. The caller resolves the label from the catalogue it
 * has loaded anyway (`activityTypeLabel`).
 */
export function activityLabel(activity, typeLabel) {
    return activity.title ?? typeLabel;
}
/** Sum of an activity's positions, billable ones only or all of them. Lives
 *  here so the list, the editor and the later invoice draft agree. */
export function sumItems(items, options = {}) {
    return items
        .filter((item) => !options.billableOnly || item.billable)
        .reduce((total, item) => total + item.quantity * item.unitPriceCents, 0);
}
//# sourceMappingURL=activity.js.map